import { useCallback, useEffect, useRef, useState } from "react";
import type { Event } from "stream-chat";
import { useChatContext } from "stream-chat-react";

interface MentionTrackingState {
  mentionedCids: Set<string>;
  latestMentionIds: Map<string, string>;
  isLoading: boolean;
}

const getStoredMentionCids = (storageKey: string | null): Set<string> => {
  if (!storageKey) return new Set();

  try {
    const stored = JSON.parse(localStorage.getItem(storageKey) ?? "[]");
    if (!Array.isArray(stored)) return new Set();
    return new Set(stored.filter((cid): cid is string => typeof cid === "string"));
  } catch {
    return new Set();
  }
};

// Tracks every channel where the current user has been mentioned, including
// mentions that have already been read. Stream history is the source of truth;
// localStorage is only used to avoid an empty list while history is loading.
export const useMentionTracking = (userId: string): MentionTrackingState => {
  const { client } = useChatContext();
  const mentionedStorageKey = userId ? `hp-chat-mentioned-cids-${userId}` : null;
  const [mentionedCids, setMentionedCids] = useState<Set<string>>(() =>
    getStoredMentionCids(mentionedStorageKey),
  );
  const [latestMentionIds, setLatestMentionIds] = useState<Map<string, string>>(
    new Map(),
  );
  const [isLoading, setIsLoading] = useState(Boolean(userId));
  const liveMentionCidsRef = useRef<Set<string>>(new Set());
  const liveMentionIdsRef = useRef<Map<string, string>>(new Map());

  // Tick counter to force re-evaluation of mention splits on real-time events
  const [, setMentionTick] = useState(0);
  const bumpMentionTick = useCallback(() => setMentionTick((t) => t + 1), []);
  // Tick counter to force re-evaluation of pin splits on real-time events
  const [, setPinnedTick] = useState(0);
  const bumpPinnedTick = useCallback(() => setPinnedTick((t) => t + 1), []);

  // Real-time: bump tick when mention counts may change
  useEffect(() => {
    if (!client) return;
    const handler = () => bumpMentionTick();
    client.on("message.new", handler);
    client.on("message.read", handler);
    client.on("notification.mark_read", handler);
    client.on("notification.message_new", handler);
    return () => {
      client.off("message.new", handler);
      client.off("message.read", handler);
      client.off("notification.mark_read", handler);
      client.off("notification.message_new", handler);
    };
  }, [client, bumpMentionTick]);

  // Real-time: bump tick when pin state changes
  useEffect(() => {
    if (!client) return;
    const handler = () => bumpPinnedTick();
    client.on("member.updated", handler);
    return () => {
      client.off("member.updated", handler);
    };
  }, [client, bumpPinnedTick]);

  // Refresh the cache when the signed-in user changes.
  useEffect(() => {
    liveMentionCidsRef.current = new Set();
    liveMentionIdsRef.current = new Map();
    setMentionedCids(getStoredMentionCids(mentionedStorageKey));
    setLatestMentionIds(new Map());
  }, [mentionedStorageKey]);

  useEffect(() => {
    if (!client || !userId) return;

    const addCid = (cid: string, messageId?: string) => {
      if (!cid || !mentionedStorageKey) return;
      liveMentionCidsRef.current.add(cid);
      if (messageId) {
        liveMentionIdsRef.current.set(cid, messageId);
        setLatestMentionIds((prev) => {
          const next = new Map(prev);
          next.set(cid, messageId);
          return next;
        });
      }
      setMentionedCids((prev) => {
        if (prev.has(cid)) return prev;
        const next = new Set(prev);
        next.add(cid);
        try {
          localStorage.setItem(mentionedStorageKey, JSON.stringify([...next]));
        } catch {
          // The in-memory set still works when browser storage is unavailable.
        }
        return next;
      });
    };
    // notification.mentioned_in_message fires whether the tab is open or not
    const onMentioned = (event: Event) => {
      const cid = event.cid ?? event.channel?.cid ?? event.message?.cid;
      if (cid) addCid(cid, event.message?.id);
    };
    // message.new fires when the channel is being watched (tab open)
    const onNew = (event: Event) => {
      if (event.message?.mentioned_users?.some((user) => user.id === userId)) {
        const cid = event.message.cid ?? event.cid;
        if (cid) addCid(cid, event.message.id);
      }
    };
    client.on("notification.mentioned_in_message", onMentioned);
    client.on("message.new", onNew);
    return () => {
      client.off("notification.mentioned_in_message", onMentioned);
      client.off("message.new", onNew);
    };
  }, [client, userId, mentionedStorageKey]);

  // Rebuild the set from Stream so read mentions and mentions received on
  // another browser/device are included. Cursor pagination avoids silently
  // limiting the filter to only the most recent search page.
  useEffect(() => {
    if (!client || !userId || !mentionedStorageKey) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const loadMentionHistory = async () => {
      const historicalCids = new Set<string>();
      const latestHistoricalMentionIds = new Map<string, string>();
      let next: string | undefined;

      try {
        do {
          const response = await client.search(
            {
              type: "messaging",
              members: { $in: [userId] },
              archived: false,
            },
            { "mentioned_users.id": { $contains: userId } },
            {
              limit: 100,
              next,
              sort: { created_at: -1 },
            },
          );

          response.results.forEach(({ message }) => {
            if (!message.cid) return;
            historicalCids.add(message.cid);
            if (!latestHistoricalMentionIds.has(message.cid)) {
              latestHistoricalMentionIds.set(message.cid, message.id);
            }
          });
          next = response.next;
        } while (next && !cancelled);

        if (cancelled) return;

        liveMentionCidsRef.current.forEach((cid) => historicalCids.add(cid));
        liveMentionIdsRef.current.forEach((messageId, cid) => {
          latestHistoricalMentionIds.set(cid, messageId);
        });
        setMentionedCids(historicalCids);
        setLatestMentionIds(latestHistoricalMentionIds);
        try {
          localStorage.setItem(
            mentionedStorageKey,
            JSON.stringify([...historicalCids]),
          );
        } catch {
          // The fetched set remains available for the current session.
        }
      } catch (error) {
        console.warn("[Chat] Failed to load mention history:", error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void loadMentionHistory();
    return () => {
      cancelled = true;
    };
  }, [client, userId, mentionedStorageKey]);

  return { mentionedCids, latestMentionIds, isLoading };
};
